import { FLOW_GROUP_PREFIXES, getFlowUserGroupPrefix } from './constants';
import type { AuthUser } from './types';

function readRawGroups(authUser: AuthUser | null): string[] | null {
  if (!authUser) return null;
  return authUser.groups ?? null;
}

export function isLocalDevelopmentHostname(hostname: string | null | undefined): boolean {
  const normalized = (hostname || '').trim().toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '[::1]' ||
    normalized.endsWith('.localhost')
  );
}

function getRuntimeHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname || '';
}

export function getAuthGroups(authUser: AuthUser | null): string[] {
  return readRawGroups(authUser) ?? [];
}

export function hasAuthGroup(authUser: AuthUser | null, groupName: string): boolean {
  return getAuthGroups(authUser).includes(groupName);
}

export function hasFlowAccess(authUser: AuthUser | null): boolean {
  if (isLocalDevelopmentAuth(authUser)) return true;
  const prefix = getFlowUserGroupPrefix();
  const groups = getAuthGroups(authUser);
  return groups.some((g) => g === prefix || g.startsWith(`${prefix}-`));
}

// Returns list of flow keys the user has access to, for use in access denied page
export function getAccessibleFlows(authUser: AuthUser | null): string[] {
  if (isLocalDevelopmentAuth(authUser)) return Object.keys(FLOW_GROUP_PREFIXES);
  const groups = getAuthGroups(authUser);
  return Object.entries(FLOW_GROUP_PREFIXES)
    .filter(([, prefix]) => groups.some((g) => g === prefix || g.startsWith(`${prefix}-`)))
    .map(([flow]) => flow);
}

export function isLocalDevelopmentAuth(authUser: AuthUser | null): boolean {
  if (!authUser) return false;
  if (!isLocalDevelopmentHostname(getRuntimeHostname())) return false;
  const userIsNull = authUser.user == null;
  const rawGroups = readRawGroups(authUser);
  const groupsAreNull = rawGroups == null;
  return userIsNull && groupsAreNull;
}
